import _ from 'lodash'
import moment from 'moment'
import {
  ConcreteThreashold,
  ConcreteThreasholdById,
  Device,
  LogRaw,
  Session,
  ThunkAction,
  User,
} from '../../types'
import { download } from '../../utils'
// import { loadLogs } from '../Firebase/operations'
import { loadSession, loadSessionLogs } from '../Firebase/operations'
import * as actions from './actions'
import * as selectors from './selectors'

const calcDevice = (device: Device, devicePre?: Device): Device => {
  if (!devicePre) {
    return device
  }

  return {
    ...device,
    lastLogs: device.lastLogs,
  }
}

export function saveDevice(device: Device): ThunkAction {
  return async (dispatch, getState) => {
    const preDevice = selectors.getDevice(getState(), device.id)

    dispatch(actions.receiveDevice(calcDevice(device, preDevice)))
  }
}

type Th = NonNullable<ConcreteThreashold>

// 最新 log の sensor key から ch 数 (11 or 22) を判定する。
// 同一セッション内で ch 数が変わった場合は最後の log を採用する。
function detectChannelCount(logs: LogRaw[]): number {
  const lastLog = logs[logs.length - 1]

  if (!lastLog) return 11
  const maxId = Math.max(
    0,
    ...Object.keys(lastLog.fields.sensors || {}).map(Number),
  )

  return maxId > 11 ? 22 : 11
}

// 淡水ログ判定: しきい値に levelD (resiD) があれば淡水。表示側 isFreshWater と同一ロジック。
function detectFreshWater(threasholds: ConcreteThreasholdById): boolean {
  return Object.values(threasholds).some(t => t?.resistance.levelD != null)
}

export function makeCSV(
  logs: LogRaw[],
  threasholds: ConcreteThreasholdById,
): string {
  const channelCount = detectChannelCount(logs)
  const isFreshWater = detectFreshWater(threasholds)
  const ids = _.range(1, channelCount + 1)
  const th: { [id: string]: Th } = {}

  ids.forEach(i => {
    const thRaw = threasholds[i]

    if (thRaw === undefined) throw Error('Can\t make CSV, breaked threashold')
    th[i] = thRaw
  })

  const nums: number[][] = []

  nums.push(ids.map(i => th[i]?.resistance.levelA ?? -999))
  nums.push(ids.map(i => th[i]?.resistance.levelB ?? -999))
  nums.push(ids.map(i => th[i]?.resistance.levelC ?? -999))
  nums.push(ids.map(i => th[i]?.voltage.levelA ?? -999))
  nums.push(ids.map(i => th[i]?.time.levelA ?? -999))
  nums.push(ids.map(i => th[i]?.time.levelB ?? -999))

  // 淡水ありのときのみ、しきい値ブロック末尾に resistance.levelD (resiD) 行を追加。
  // 末尾追加にすることで行 1〜6 の位置は淡水あり/なしで不変 (既存パーサ互換)。
  if (isFreshWater) {
    nums.push(ids.map(i => th[i]?.resistance.levelD ?? -999))
  }
  const lines = nums.map(n => n.map(String).join(','))

  logs.forEach(log => {
    const time = log.timestamp ? moment(log.timestamp) : null
    const vals: number[] = []

    ids.forEach(i => {
      const sensor = log.fields.sensors[i]

      // セッション途中で ch 数が拡張された場合、過去 log は欠損する。0 埋めで継続。
      vals.push(sensor?.resistanceValue || 0)
      vals.push(sensor?.compactionTime || 0)
    })
    const cols = [
      time?.format('YYYY/MM/DD') || '',
      time?.format('HH:mm:ss') || '',
      ...vals,
    ]

    lines.push(cols.map(String).join(','))
  })

  return lines.join('\r\n')
}

export async function downloadCSV(
  user: User,
  session: Session,
  sessionPath: string,
) {
  const logs = await loadSessionLogs(user.id, sessionPath)

  let { threasholds } = session

  // sessionList はウィンドウ開いた時点の sessionStorage キャッシュ。
  // 開いた時点で current だったセッションは threasholds が未確定で undefined。
  // その後セッションが停止すると Firebase 側に threasholds が書き込まれるので
  // ローカルに無ければ取り直す。
  if (!threasholds) {
    const fresh = await loadSession(user.id, sessionPath)

    threasholds = fresh?.threasholds
  }

  if (logs.length === 0 || !threasholds) {
    alert('ログがありません')
    return
  }

  // v1 (管理No)_YYMMDD（項番2桁).csv　※ファイル例：MUDR100_001_19120201.csv
  // v2 端末名_yyyymmddhhmiss.csv

  // console.log(session)

  const filename =
    session.filename ||
    `${sessionPath.split('---')[0]}_${moment(session.createdAt * 1000).format(
      'YYYYMMDD_HHmmss',
    )}`

  download(makeCSV(logs, threasholds), filename)
}
